/**
 * PATH: packages/ui/src/components/global-search/facets.ts
 * FILE_ID: UI-GLOBALSEARCH-FACETS-001
 * PURPOSE: Group a flat, multi-source `SearchResult[]` into logical
 *          "facets" (Pages, Jobs, Emails, Kanban, Quotes, Files,
 *          Contacts, QA, …) so the palette can render a right-rail of
 *          source icons with live counts while the centre shows the
 *          direct results. Pure I→O — no React, no side effects.
 * WHY:     Operator (2026-06-04): "expand the search architecture where
 *          'Pages' come in the right like icons and the actual direct
 *          information comes in the centre". The fan-out engine already
 *          returns results tagged with `type` + `providerId`; this maps
 *          those onto a stable, ordered facet taxonomy so the rail is
 *          deterministic regardless of which provider answered first.
 * FLOW:
 *   ┌──────────────┐   ┌─────────────────┐   ┌────────────────────┐
 *   │ SearchResult[]│ → │ facetOf(result) │ → │ ordered Facet[] +  │
 *   │  (multi-src)  │   │  (type→facet)   │   │ counts (for rail)  │
 *   └──────────────┘   └─────────────────┘   └────────────────────┘
 * RELATED:
 *   - SearchFacetRail.tsx: renders the Facet[] this module produces
 *   - useSearch.ts: owns `activeFacet` + derives `visibleResults`
 *   - types.ts: SearchResult shape (type, providerId)
 */

import type { SearchResult } from './types'

/** Stable, user-facing source buckets. The string value is the
 *  `Facet.key` carried through the rail + `activeFacet`. */
export type FacetKey =
  | 'pages'
  | 'jobs'
  | 'emails'
  | 'kanban'
  | 'quotes'
  | 'files'
  | 'contacts'
  | 'qa'
  | 'other'

export interface FacetMeta {
  key: FacetKey
  label: string
  /** Lucide icon string name resolved by `getIcon(...)` in the rail. */
  icon: string
  /** Colour token resolved by `getColorClass(...)` in the rail. */
  color: string
  /** Lower sorts first in the rail. */
  order: number
}

export interface Facet extends FacetMeta {
  count: number
}

/**
 * The facet taxonomy. Order here is the rail order. Icons/colours
 * mirror the per-type visuals the providers already emit so the
 * rail reads as the same "language" as the centre rows.
 */
export const FACET_META: Record<FacetKey, FacetMeta> = {
  pages: { key: 'pages', label: 'Pages', icon: 'Compass', color: 'teal', order: 0 },
  jobs: { key: 'jobs', label: 'Jobs', icon: 'Briefcase', color: 'blue', order: 1 },
  emails: { key: 'emails', label: 'Emails', icon: 'Mail', color: 'orange', order: 2 },
  kanban: { key: 'kanban', label: 'Kanban', icon: 'LayoutGrid', color: 'green', order: 3 },
  quotes: { key: 'quotes', label: 'Quotes', icon: 'FileText', color: 'purple', order: 4 },
  files: { key: 'files', label: 'Files', icon: 'File', color: 'emerald', order: 5 },
  contacts: { key: 'contacts', label: 'Contacts', icon: 'User', color: 'purple', order: 6 },
  qa: { key: 'qa', label: 'QA', icon: 'ClipboardCheck', color: 'orange', order: 7 },
  other: { key: 'other', label: 'Other', icon: 'File', color: 'gray', order: 99 },
}

/** Map a result `type` (open string union) onto a facet key. The
 *  navigation provider emits `type: 'page'`; kanban emits
 *  card/board/lane; everything else maps 1:1 or falls to `other`. */
const TYPE_TO_FACET: Record<string, FacetKey> = {
  page: 'pages',
  nav: 'pages',
  navigation: 'pages',
  job: 'jobs',
  email: 'emails',
  card: 'kanban',
  board: 'kanban',
  lane: 'kanban',
  quote: 'quotes',
  file: 'files',
  contact: 'contacts',
  inspection: 'qa',
  ncr: 'qa',
}

/**
 * Resolve the facet bucket for a single result. We prefer the
 * result `type`, then fall back to a small set of well-known
 * `providerId`s (so a custom provider that emits an unknown type
 * but a known id still buckets sensibly), then `other`.
 */
export function facetOf(result: SearchResult): FacetKey {
  const byType = TYPE_TO_FACET[result.type]
  if (byType) return byType
  const pid = (result.providerId ?? '').toLowerCase()
  if (pid === 'navigation' || pid === 'pages') return 'pages'
  if (pid === 'jobs') return 'jobs'
  if (pid === 'email' || pid === 'emails') return 'emails'
  if (pid === 'kanban') return 'kanban'
  return 'other'
}

/**
 * Compute the ordered facet list (with counts) for a result set.
 * Only facets with ≥1 result are returned — the rail never shows
 * an empty source. Sorted by `FACET_META.order`.
 */
export function computeFacets(results: SearchResult[]): Facet[] {
  const counts = new Map<FacetKey, number>()
  for (const r of results) {
    const key = facetOf(r)
    counts.set(key, (counts.get(key) ?? 0) + 1)
  }
  const out: Facet[] = []
  for (const [key, count] of counts.entries()) {
    if (count <= 0) continue
    out.push({ ...FACET_META[key], count })
  }
  out.sort((a, b) => a.order - b.order)
  return out
}

/**
 * Filter a result set to a single facet. `null` → no filter
 * (returns the same array reference so callers can cheaply skip
 * re-render churn / keep keyboard-nav indices stable).
 */
export function filterByFacet(
  results: SearchResult[],
  activeFacet: FacetKey | null,
): SearchResult[] {
  if (!activeFacet) return results
  return results.filter((r) => facetOf(r) === activeFacet)
}
